import { useState, useEffect } from 'react';
import { adminService } from '../services/adminService';
import { Role, CreateRoleRequest, UpdateRolePermissionsRequest } from '../types/admin';

interface UseAdminRolesReturn {
  roles: Role[];
  loading: boolean;
  error: string | null;
  createRole: (data: CreateRoleRequest) => Promise<void>;
  updateRolePermissions: (id: string, data: UpdateRolePermissionsRequest) => Promise<void>;
  deleteRole: (id: string) => Promise<void>;
  refreshRoles: () => Promise<void>;
}

export const useAdminRoles = (): UseAdminRolesReturn => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getRoles();
      setRoles(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch roles';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const createRole = async (data: CreateRoleRequest) => {
    try {
      setError(null);
      const newRole = await adminService.createRole(data);
      setRoles([...roles, newRole]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create role';
      setError(message);
      throw err;
    }
  };

  const updateRolePermissions = async (id: string, data: UpdateRolePermissionsRequest) => {
    try {
      setError(null);
      const updatedRole = await adminService.updateRolePermissions(id, data);
      setRoles(roles.map(r => (r.id === id ? updatedRole : r)));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update role permissions';
      setError(message);
      throw err;
    }
  };

  const deleteRole = async (id: string) => {
    try {
      setError(null);
      await adminService.deleteRole(id);
      setRoles(roles.filter(r => r.id !== id));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete role';
      setError(message);
      throw err;
    }
  };

  const refreshRoles = async () => {
    await fetchRoles();
  };

  return {
    roles,
    loading,
    error,
    createRole,
    updateRolePermissions,
    deleteRole,
    refreshRoles,
  };
};

